import { Award } from "lucide-react";
import { useGetIdentity, useTranslate } from "ra-core";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { BUSINESS_ARM_LABELS } from "../sales/businessArms";
import { BusinessUnitReviewBadge } from "../sales/BusinessUnitReviewBadge";
import { ROLE_LABELS } from "../sales/roles";
import { TeamContactActions } from "../team/TeamContactActions";
import type { OrgChartPerson } from "../types";
import {
  ORG_CHART_ARM_LABELS,
  getPositionLabel,
  type OrgChartRelationship,
} from "./orgChartLayout";

export interface OrgChartPersonCardProps {
  /** `null` while closed/no selection -- the dialog stays mounted but empty. */
  person: OrgChartPerson | null;
  /** Empty when neither applies. An asesora can have several at once -- her
   * reports_to Coordinación/Acompañamiento AND one Acompañamiento per
   * distinct project Líder BPO. */
  relationships: OrgChartRelationship[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Almikia Epic EO (TASK-098): the person card opened by tapping an
 * Organigrama row -- position, brazo, projects (marked "Líder BPO" where
 * `is_lider_for_project`), the Coordinación/Acompañamiento relationship, and
 * a one-tap mailto contact. The level row is a neutral badge shown when the
 * view carries `level_name`; the clave row is shown to every viewer when the
 * record carries one (claves are not secret, owner Q11); the phone row shows
 * whatever the view returned (the view applies its own restriction). A legacy
 * 'ambos' review badge shows to Direccion/Lead Manager only, and
 * `<TeamContactActions>` adds the WhatsApp/call/email quick links. Built on the
 * shared shadcn `<Dialog>` (Escape/backdrop/close button close it,
 * keyboard-reachable, matching every other dialog in the app).
 */
export const OrgChartPersonCard = ({
  person,
  relationships,
  open,
  onOpenChange,
}: OrgChartPersonCardProps) => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  if (!person) return null;

  // The legacy 'ambos' review marker is for Direccion and Lead Manager only.
  const canSeeReview =
    identity?.role === "direccion" || identity?.role === "lead_manager";

  const fullName = `${person.first_name} ${person.last_name}`;
  const positionLabel = getPositionLabel(person);
  const armLabel = person.business_arm
    ? BUSINESS_ARM_LABELS[person.business_arm]
    : ORG_CHART_ARM_LABELS.unassigned;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 text-left">
          <Avatar className="size-12 shrink-0">
            <AvatarImage src={person.avatar?.src ?? undefined} />
            <AvatarFallback>
              {person.first_name.charAt(0).toUpperCase()}
              {person.last_name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <DialogTitle className="truncate text-lg">{fullName}</DialogTitle>
            <DialogDescription className="truncate text-base">
              {positionLabel}
            </DialogDescription>
          </div>
        </DialogHeader>

        <dl className="flex flex-col gap-3 text-base">
          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted-foreground">
              {translate("crm.org_chart.card.arm_label")}
            </dt>
            <dd className="font-medium text-right">{armLabel}</dd>
          </div>

          {person.projects.length > 0 ? (
            <div className="flex flex-col gap-1">
              <dt className="text-muted-foreground">
                {translate("crm.org_chart.card.projects_label")}
              </dt>
              <dd>
                <ul className="flex flex-col gap-1">
                  {person.projects.map((project) => (
                    <li
                      key={project.project_id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{project.name}</span>
                      {project.is_lider_for_project ? (
                        <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[13px] font-medium">
                          {ROLE_LABELS.lider}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}

          {relationships.length > 0 ? (
            <div className="flex flex-col gap-1">
              <dt className="text-muted-foreground">
                {translate("crm.org_chart.card.relationship_label")}
              </dt>
              <dd>
                <ul className="flex flex-col gap-1">
                  {relationships.map((relationship) => (
                    <li
                      key={`${relationship.label}-${relationship.counterpart.id}`}
                      className="text-right font-medium"
                    >
                      {relationship.label} ·{" "}
                      {relationship.counterpart.first_name}{" "}
                      {relationship.counterpart.last_name}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}

          {person.phone ? (
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">
                {translate("crm.org_chart.card.phone_label")}
              </dt>
              <dd className="font-medium tabular-nums text-right">
                {person.phone}
              </dd>
            </div>
          ) : null}

          {person.advisor_code ? (
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">
                {translate("crm.org_chart.card.advisor_code_label")}
              </dt>
              <dd className="font-medium tabular-nums text-right">
                {person.advisor_code}
              </dd>
            </div>
          ) : null}

          {person.level_name ? (
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">
                {translate("crm.org_chart.card.level_label")}
              </dt>
              <dd>
                <Badge variant="secondary" className="gap-1 text-[13px]">
                  <Award aria-hidden="true" className="size-3" />
                  {person.level_name}
                </Badge>
              </dd>
            </div>
          ) : null}

          {person.business_unit_needs_review && canSeeReview ? (
            <div className="flex justify-end">
              <BusinessUnitReviewBadge />
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted-foreground">
              {translate("crm.org_chart.card.email_label")}
            </dt>
            <dd>
              <a
                href={`mailto:${person.email}`}
                className="font-medium text-primary underline-offset-2 hover:underline"
              >
                {person.email}
              </a>
            </dd>
          </div>
        </dl>

        <TeamContactActions salesId={person.id} email={person.email} />
      </DialogContent>
    </Dialog>
  );
};
